import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '../api/api';
import { 
  RefreshCw, 
  Filter, 
  Calendar, 
  Truck, 
  Package, 
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronDown,
  Check,
  Eye,
  Package2
} from 'lucide-react';
import './PackageShipment.css';
import GradientHeading from './GradientHeading';

// Package status mapping for TikTok API statuses
const packageStatusMap = {
    'PENDING': {
        label: 'Pending',
        description: 'Package is being prepared for shipment',
        type: 'pending'
    },
    'PROCESSING': {
        label: 'Processing',
        description: 'Package is being processed by carrier',
        type: 'processing'
    },
    'SHIPPED': {
        label: 'Shipped',
        description: 'Package has been shipped and is in transit',
        type: 'processing'
    },
    'IN_TRANSIT': {
        label: 'In Transit',
        description: 'Package is on its way to destination',
        type: 'processing'
    },
    'OUT_FOR_DELIVERY': {
        label: 'Out for Delivery',
        description: 'Package is out for delivery today',
        type: 'processing'
    },
    'DELIVERED': {
        label: 'Delivered',
        description: 'Package has been successfully delivered',
        type: 'completed'
    },
    'EXCEPTION': {
        label: 'Exception',
        description: 'Delivery issue - address problem or delivery attempt failed',
        type: 'exception'
    },
    'RETURNED': {
        label: 'Returned',
        description: 'Package was returned to sender',
        type: 'returned'
    },
    'CANCELLED': {
        label: 'Cancelled',
        description: 'Package shipment was cancelled',
        type: 'cancelled'
    }
};

const PackageShipment = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState('');
    const [statistics, setStatistics] = useState({});
    const [expandedPackages, setExpandedPackages] = useState(new Set());
    const isInitialMount = useRef(true);

    // Filter states
    const [dateRange, setDateRange] = useState('last_week');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState(['PENDING', 'PROCESSING', 'SHIPPED', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'EXCEPTION', 'RETURNED', 'CANCELLED']);
    const [selectedProviders, setSelectedProviders] = useState([]);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);

    const statusFilterOptions = [
        { value: 'PENDING', label: 'Pending' },
        { value: 'PROCESSING', label: 'Processing' },
        { value: 'SHIPPED', label: 'Shipped' },
        { value: 'IN_TRANSIT', label: 'In Transit' },
        { value: 'OUT_FOR_DELIVERY', label: 'Out for Delivery' },
        { value: 'DELIVERED', label: 'Delivered' },
        { value: 'EXCEPTION', label: 'Exception' },
        { value: 'RETURNED', label: 'Returned' },
        { value: 'CANCELLED', label: 'Cancelled' }
    ];

    // Get available providers from current packages
    const getAvailableProviders = () => {
        const providers = new Set();
        packages.forEach(pkg => {
            const provider = pkg.shipping_provider_name;
            if (provider) {
                providers.add(provider);
            }
        });
        return Array.from(providers).map(provider => ({
            value: provider,
            label: provider
        }));
    };

    const providerOptions = getAvailableProviders();

    const fetchPackages = useCallback(async (isInitialLoad = false) => {
        const getDateRangeParams = () => {
            const now = new Date();
            const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            let startDate, endDate;

            switch (dateRange) {
                case 'yesterday':
                    startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
                    endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate() + 1);
                    break;
                case 'last_week':
                    startDate = new Date(yesterday.getTime() - 7 * 24 * 60 * 60 * 1000);
                    endDate = yesterday;
                    break;
                case 'last_month':
                    startDate = new Date(yesterday.getTime() - 30 * 24 * 60 * 60 * 1000);
                    endDate = yesterday;
                    break;
                case 'custom':
                    if (customStartDate && customEndDate) {
                        startDate = new Date(customStartDate);
                        endDate = new Date(customEndDate);
                    } else {
                        return null;
                    }
                    break;
                default:
                    startDate = new Date(yesterday.getTime() - 7 * 24 * 60 * 60 * 1000);
                    endDate = yesterday;
            }

            return {
                start_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0]
            };
        };

        if (isInitialLoad) {
            setLoading(true);
        } else {
            setFetching(true);
        }
        setError('');

        try {
            const dateParams = getDateRangeParams();
            const params = new URLSearchParams();
            
            if (dateParams) {
                params.append('start_date', dateParams.start_date);
                params.append('end_date', dateParams.end_date);
            }

            const response = await apiClient.get(`/shipments?${params.toString()}`);
            
            if (response.data && response.data.status === 'success') {
                const apiPackages = response.data.data.packages || [];
                const filteredPackages = apiPackages.filter(pkg => {
                    const statusMatch = selectedStatuses.includes(pkg.status);
                    const providerMatch = selectedProviders.length === 0 || 
                                        selectedProviders.includes(pkg.shipping_provider_name);
                    return statusMatch && providerMatch;
                });
                
                setPackages(filteredPackages);
                setStatistics(response.data.data.statistics || {});
                
                // Update selected providers if empty (first load)
                if (selectedProviders.length === 0 && apiPackages.length > 0) {
                    const providers = new Set();
                    apiPackages.forEach(pkg => {
                        if (pkg.shipping_provider_name) {
                            providers.add(pkg.shipping_provider_name);
                        }
                    });
                    setSelectedProviders(Array.from(providers));
                }
            } else {
                throw new Error(response.data?.message || 'Failed to fetch package data');
            }
        } catch (err) {
            console.error('Error fetching packages:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to fetch package data');
        } finally {
            setLoading(false);
            setFetching(false);
        }
    }, [selectedStatuses, selectedProviders, dateRange, customStartDate, customEndDate]);

    useEffect(() => {
        if (isInitialMount.current) {
            fetchPackages(true);
            isInitialMount.current = false;
        } else {
            fetchPackages(false);
        }
    }, [fetchPackages]);

    // Set the browser tab title
    useEffect(() => {

    }, []);

    // Handle clicking outside dropdown
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setStatusDropdownOpen(false);
                setProviderDropdownOpen(false);
            }
        };

        if (statusDropdownOpen || providerDropdownOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [statusDropdownOpen, providerDropdownOpen]);

    const handleStatusChange = (status) => {
        setSelectedStatuses(prev => 
            prev.includes(status) 
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const handleProviderChange = (provider) => {
        setSelectedProviders(prev => 
            prev.includes(provider) 
                ? prev.filter(c => c !== provider)
                : [...prev, provider]
        );
    };

    const handleSelectAllStatuses = () => {
        setSelectedStatuses(statusFilterOptions.map(option => option.value));
    };

    const handleClearAllStatuses = () => {
        setSelectedStatuses([]);
    };

    const handleSelectAllProviders = () => {
        setSelectedProviders(providerOptions.map(option => option.value));
    };

    const handleClearAllProviders = () => {
        setSelectedProviders([]);
    };

    const togglePackageExpansion = (packageId) => {
        setExpandedPackages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(packageId)) {
                newSet.delete(packageId);
            } else {
                newSet.add(packageId);
            }
            return newSet;
        });
    };

    const getStatusDropdownDisplayText = () => {
        if (selectedStatuses.length === 0) {
            return 'Select status filters...';
        } else if (selectedStatuses.length === statusFilterOptions.length) {
            return 'All statuses selected';
        } else if (selectedStatuses.length === 1) {
            const option = statusFilterOptions.find(opt => opt.value === selectedStatuses[0]);
            return option?.label || selectedStatuses[0];
        } else {
            return `${selectedStatuses.length} statuses selected`;
        }
    };

    const getProviderDropdownDisplayText = () => {
        if (selectedProviders.length === 0) {
            return 'Select providers...';
        } else if (selectedProviders.length === providerOptions.length) {
            return 'All providers selected';
        } else if (selectedProviders.length === 1) {
            const option = providerOptions.find(opt => opt.value === selectedProviders[0]);
            return option?.label || selectedProviders[0];
        } else {
            return `${selectedProviders.length} providers selected`;
        }
    };

    const getStatusBadge = (status) => {
        const statusInfo = packageStatusMap[status];
        if (!statusInfo) {
            return 'status-badge status-pending';
        }
        
        const typeClasses = {
            'pending': 'status-badge status-pending',
            'processing': 'status-badge status-processing',
            'completed': 'status-badge status-completed',
            'exception': 'status-badge status-exception',
            'returned': 'status-badge status-returned',
            'cancelled': 'status-badge status-exception'
        };
        
        return typeClasses[statusInfo.type] || 'status-badge status-pending';
    };

    const getStatusDisplay = (status) => {
        const statusInfo = packageStatusMap[status];
        return statusInfo ? statusInfo.label : status;
    };

    const getStatusDescription = (status) => {
        const statusInfo = packageStatusMap[status];
        return statusInfo ? statusInfo.description : '';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTrackingEvent = (event) => {
        if (!event.update_time_millis) return 'N/A';
        return new Date(event.update_time_millis).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) return (
        <div className="package-shipment-container">
            <div className="loading">
                <RefreshCw className="animate-spin" style={{ width: 32, height: 32, margin: '0 auto 16px' }} />
                Loading package shipments...
            </div>
        </div>
    );
    
    if (error) return (
        <div className="package-shipment-container">
            <div className="error">
                <AlertCircle style={{ width: 32, height: 32, margin: '0 auto 16px' }} />
                Error: {error}
            </div>
        </div>
    );

    return (
        <div className="package-shipment-container">
            {/* Header Section */}
            <div className="header-section">
                <GradientHeading>Package Shipment Tracking</GradientHeading>
                <p className="page-subtitle">
                    Monitor and track package shipments and delivery status
                </p>
            </div>
            
            {/* Filters Section */}
            <div className="controls-section">
                <div className="controls-left">
                    {/* Date Range Filter */}
                    <div className="control-group">
                        <label><Calendar size={16} style={{ display: 'inline', marginRight: 4 }} /> Date Range</label>
                        <select 
                            value={dateRange} 
                            onChange={(e) => setDateRange(e.target.value)}
                            className="select-input"
                        >
                            <option value="yesterday">Yesterday</option>
                            <option value="last_week">Last Week</option>
                            <option value="last_month">Last Month</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {dateRange === 'custom' && (
                            <div className="custom-date-range">
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="date-input"
                                    placeholder="Start Date"
                                />
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="date-input"
                                    placeholder="End Date"
                                />
                            </div>
                        )}
                    </div>

                    {/* Status Filter */}
                    <div className="control-group">
                        <label><Filter size={16} style={{ display: 'inline', marginRight: 4 }} /> Status</label>
                        <div className="dropdown-section">
                            <div className="dropdown-container">
                                {statusDropdownOpen && <div className="dropdown-backdrop" onClick={() => setStatusDropdownOpen(false)} />}
                                
                                <div 
                                    className={`dropdown-trigger ${statusDropdownOpen ? 'open' : ''}`}
                                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                                >
                                    <span className={`dropdown-text ${selectedStatuses.length === 0 ? 'placeholder' : ''}`}>
                                        {getStatusDropdownDisplayText()}
                                    </span>
                                    <ChevronDown size={16} className={`dropdown-arrow ${statusDropdownOpen ? 'open' : ''}`} />
                                </div>

                                {statusDropdownOpen && (
                                    <div className="dropdown-menu">
                                        <div className="dropdown-header">
                                            <button onClick={handleSelectAllStatuses} className="dropdown-header-button">
                                                Select All
                                            </button>
                                            <button onClick={handleClearAllStatuses} className="dropdown-header-button">
                                                Clear All
                                            </button>
                                        </div>
                                        <div className="dropdown-options">
                                            {statusFilterOptions.map(option => (
                                                <div 
                                                    key={option.value} 
                                                    className="dropdown-option"
                                                    onClick={() => handleStatusChange(option.value)}
                                                >
                                                    <div className={`dropdown-checkbox ${selectedStatuses.includes(option.value) ? 'checked' : ''}`}>
                                                        {selectedStatuses.includes(option.value) && <Check size={12} />}
                                                    </div>
                                                    <span className="dropdown-label">{option.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Provider Filter */}
                    <div className="control-group">
                        <label><Truck size={16} style={{ display: 'inline', marginRight: 4 }} /> Shipping Provider</label>
                        <div className="dropdown-section">
                            <div className="dropdown-container">
                                {providerDropdownOpen && <div className="dropdown-backdrop" onClick={() => setProviderDropdownOpen(false)} />}
                                
                                <div 
                                    className={`dropdown-trigger ${providerDropdownOpen ? 'open' : ''}`}
                                    onClick={() => setProviderDropdownOpen(!providerDropdownOpen)}
                                >
                                    <span className={`dropdown-text ${selectedProviders.length === 0 ? 'placeholder' : ''}`}>
                                        {getProviderDropdownDisplayText()}
                                    </span>
                                    <ChevronDown size={16} className={`dropdown-arrow ${providerDropdownOpen ? 'open' : ''}`} />
                                </div>

                                {providerDropdownOpen && (
                                    <div className="dropdown-menu">
                                        <div className="dropdown-header">
                                            <button onClick={handleSelectAllProviders} className="dropdown-header-button">
                                                Select All
                                            </button>
                                            <button onClick={handleClearAllProviders} className="dropdown-header-button">
                                                Clear All
                                            </button>
                                        </div>
                                        <div className="dropdown-options">
                                            {providerOptions.map(option => (
                                                <div 
                                                    key={option.value} 
                                                    className="dropdown-option"
                                                    onClick={() => handleProviderChange(option.value)}
                                                >
                                                    <div className={`dropdown-checkbox ${selectedProviders.includes(option.value) ? 'checked' : ''}`}>
                                                        {selectedProviders.includes(option.value) && <Check size={12} />}
                                                    </div>
                                                    <span className="dropdown-label">{option.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <button 
                    onClick={() => fetchPackages(false)}
                    className="fetch-button"
                    disabled={fetching}
                >
                    <RefreshCw size={16} style={{ marginRight: 8 }} className={fetching ? 'animate-spin' : ''} />
                    {fetching ? 'Loading...' : 'Apply Filters'}
                </button>
            </div>

            {/* Statistics Section */}
            {statistics && Object.keys(statistics).length > 0 && (
                <div className="statistics-section">
                    <h3 className="statistics-title">
                        <Package size={20} style={{ display: 'inline', marginRight: 8 }} />
                        Shipment Statistics
                    </h3>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-value">{statistics.total_packages || 0}</div>
                            <div className="stat-label">Total Packages</div>
                        </div>
                        {statistics.status_counts && Object.keys(statistics.status_counts).length > 0 && (
                            <div className="stat-card">
                                <div className="stat-label" style={{ marginBottom: 12 }}>Status Breakdown</div>
                                <div className="status-breakdown">
                                    {Object.entries(statistics.status_counts).map(([status, count]) => (
                                        <div key={status} className="status-item" title={getStatusDescription(status)}>
                                            <span className="status-item-label">{getStatusDisplay(status)}</span>
                                            <span className="status-item-value">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {statistics.provider_counts && Object.keys(statistics.provider_counts).length > 0 && (
                            <div className="stat-card">
                                <div className="stat-label" style={{ marginBottom: 12 }}>Provider Breakdown</div>
                                <div className="status-breakdown">
                                    {Object.entries(statistics.provider_counts).map(([provider, count]) => (
                                        <div key={provider} className="status-item">
                                            <span className="status-item-label">{provider}</span>
                                            <span className="status-item-value">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Packages List */}
            <div className="shipments-section">
                <div className="shipments-header">
                    <h3 className="shipments-title">
                        <Package size={20} style={{ display: 'inline', marginRight: 8 }} />
                        Package Shipments
                    </h3>
                    <div className="shipments-count">{packages.length}</div>
                </div>

                <div className="table-container">
                    <table className="shipments-table">
                        <thead>
                            <tr>
                                <th>Package Details</th>
                                <th>Shipping Provider</th>
                                <th>Status</th>
                                <th>Dates</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {packages.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="no-data-cell">
                                        <div className="no-data">
                                            <Package className="no-data-icon" />
                                            <p>No package shipments found for the selected criteria.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                packages.map(pkg => (
                                    <React.Fragment key={pkg.package_id}>
                                        <tr>
                                            <td>
                                                <div className="package-details">
                                                    <div className="package-id">#{pkg.package_id}</div>
                                                    <div className="package-meta">
                                                        Tracking: {pkg.tracking_number || 'N/A'}<br/>
                                                        {pkg.orders?.length > 0 && (
                                                            <>Orders: {pkg.orders.length}<br/></>
                                                        )}
                                                        {pkg.orders?.reduce((total, order) => total + (order.skus?.length || 0), 0) > 0 && (
                                                            <>SKUs: {pkg.orders.reduce((total, order) => total + (order.skus?.length || 0), 0)}</>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="carrier-info">
                                                    <div className="carrier-name">{pkg.shipping_provider_name || 'Unknown'}</div>
                                                    <div className="shipping-method">ID: {pkg.shipping_provider_id || 'N/A'}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <span 
                                                    className={getStatusBadge(pkg.status)}
                                                    title={getStatusDescription(pkg.status)}
                                                >
                                                    {getStatusDisplay(pkg.status)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="delivery-info">
                                                    <div className="created-date">
                                                        <Clock size={14} style={{ marginRight: 4, display: 'inline' }} />
                                                        Created: {formatDate(pkg.create_time)}
                                                    </div>
                                                    <div className="updated-date">
                                                        <CheckCircle size={14} style={{ marginRight: 4, display: 'inline', color: '#10b981' }} />
                                                        Updated: {formatDate(pkg.update_time)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => togglePackageExpansion(pkg.package_id)}
                                                    className="expand-button"
                                                >
                                                    <Eye size={16} style={{ marginRight: 4 }} />
                                                    {expandedPackages.has(pkg.package_id) ? 'Hide Details' : 'View Details'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedPackages.has(pkg.package_id) && (
                                            <tr>
                                                <td colSpan="5" className="expanded-details">
                                                    <div className="package-expansion">
                                                        {/* Orders and SKUs */}
                                                        {pkg.orders && pkg.orders.length > 0 && (
                                                            <div className="orders-section">
                                                                <h4><Package2 size={16} style={{ marginRight: 6 }} />Orders & SKUs</h4>
                                                                {pkg.orders.map(order => (
                                                                    <div key={order.order_id} className="order-card">
                                                                        <div className="order-header">
                                                                            <strong>Order ID: {order.order_id}</strong>
                                                                        </div>
                                                                        {order.skus && order.skus.length > 0 && (
                                                                            <div className="skus-grid">
                                                                                {order.skus.map(sku => (
                                                                                    <div key={sku.id} className="sku-card">
                                                                                        {sku.image_url && (
                                                                                            <img 
                                                                                                src={sku.image_url} 
                                                                                                alt={sku.name}
                                                                                                className="sku-image"
                                                                                                onError={(e) => {
                                                                                                    e.target.style.display = 'none';
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                        <div className="sku-info">
                                                                                            <div className="sku-name">{sku.name}</div>
                                                                                            <div className="sku-quantity">Qty: {sku.quantity}</div>
                                                                                            <div className="sku-id">SKU ID: {sku.id}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Tracking Events */}
                                                        {pkg.tracking_events && pkg.tracking_events.length > 0 && (
                                                            <div className="tracking-section">
                                                                <h4><Truck size={16} style={{ marginRight: 6 }} />Tracking History</h4>
                                                                <div className="tracking-timeline">
                                                                    {pkg.tracking_events
                                                                        .sort((a, b) => (b.update_time_millis || 0) - (a.update_time_millis || 0))
                                                                        .map((event, index) => (
                                                                        <div key={index} className="tracking-event">
                                                                            <div className="tracking-date">
                                                                                {formatTrackingEvent(event)}
                                                                            </div>
                                                                            <div className="tracking-description">
                                                                                {event.description || 'Status update'}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PackageShipment;
